import type { VenueRoomStatus } from '@pulsify/shared/dto'
import { useCallback } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import { useVenueAnnouncement, useVenueRoomStatus } from '~/entities/listing/model/use-venue-room'
import { FlyerDialog } from '~/features/events/checkin-flyer/checkin-flyer-dialog'
import type { FlyerLocale, FlyerMotif } from '~/features/events/checkin-flyer/flyer-content'
import { buildVenueFlyerContent } from '~/features/events/checkin-flyer/flyer-content'
import { LiveRoomQr } from '~/features/events/components/event-checkin-qr-card'
import { AnnouncementBox } from '~/features/room-announcements/components/announcement-box'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import type { Listing } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { InfoTooltip } from '~/shared/ui/info-tooltip'
import { Separator } from '~/shared/ui/separator'
import { Skeleton } from '~/shared/ui/skeleton'

const VENUE_MOTIFS: FlyerMotif[] = ['onsite']

export function VenueRoomCard({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: listing } = useListing(businessId, listingId)
  const status = useVenueRoomStatus(businessId, listingId)

  if (!business || !listing) return null

  return (
    <Card className="flex w-full flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <h3 className="font-serif font-semibold">{m.venueRoomTitle()}</h3>
          <InfoTooltip label={m.venueRoomTitle()}>{m.venueRoomDescription()}</InfoTooltip>
        </div>
        {status.data && <LiveState status={status.data} />}
      </div>
      {status.isLoading ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : status.isError || !status.data ? (
        <>
          <p className="text-destructive text-sm">{m.checkinQrLoadFailed()}</p>
          <Button
            variant="outline"
            className="w-full"
            disabled={status.isFetching}
            onClick={() => status.refetch()}
          >
            {m.tryAgain()}
          </Button>
        </>
      ) : (
        <VenueRoomBody businessId={business.id} listing={listing} status={status.data} />
      )}
    </Card>
  )
}

function LiveState({ status }: { status: VenueRoomStatus }) {
  const live = status.eventId !== null
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground text-xs">
      <span
        aria-hidden
        className={cn('size-2 rounded-full', live ? 'bg-brand' : 'bg-muted-foreground/40')}
      />
      {live ? m.liveRoomHereNow({ count: status.here }) : m.liveRoomNoneOpen()}
    </span>
  )
}

function VenueRoomBody({
  businessId,
  listing,
  status,
}: {
  businessId: string
  listing: Listing
  status: VenueRoomStatus
}) {
  const announce = useVenueAnnouncement(businessId, listing.id)
  const code = status.code
  const url = status.url
  const buildContent = useCallback(
    (motif: FlyerMotif, locale: FlyerLocale) =>
      buildVenueFlyerContent(listing, { code, url }, motif, locale),
    [listing, code, url],
  )

  return (
    <>
      <LiveRoomQr
        checkin={{ code, url }}
        imageAlt={m.venueRoomQrAlt({ venueName: listing.name })}
        flyer={<FlyerDialog motifs={VENUE_MOTIFS} buildContent={buildContent} compact />}
      />
      {status.eventId && (
        <>
          <Separator className="my-1" />
          <AnnouncementBox
            here={status.here}
            announcements={status.announcements}
            isSending={announce.isPending}
            onSend={announce.mutateAsync}
          />
        </>
      )}
    </>
  )
}
